/*
  <mountain-scene> — an animated Smoky Mountain horizon drawn in a single WebGL pass.

  No libraries. The whole scene (sky, sun, drifting cloud bands, layered ridges,
  tree lines and moving mist) is generated procedurally in one fragment shader, so
  the asset stays a few kilobytes instead of the ~600KB a 3D engine would cost.

  The element progressively enhances an inline SVG that is already painted by the
  time this runs, so there is no blank frame and no layout shift. If WebGL is
  unavailable, or the visitor prefers reduced motion, the SVG simply stays.
*/
(function () {
  'use strict';

  var VERTEX = [
    'attribute vec2 a_pos;',
    'void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }'
  ].join('\n');

  var FRAGMENT = [
    'precision highp float;',

    'uniform vec2  u_res;',
    'uniform float u_time;',
    'uniform vec2  u_pointer;',
    'uniform float u_scroll;',
    'uniform vec3  u_skyTop;',
    'uniform vec3  u_skyMid;',
    'uniform vec3  u_skyLow;',
    'uniform vec3  u_sunCol;',
    'uniform vec3  u_ridgeFar;',
    'uniform vec3  u_ridgeNear;',
    'uniform vec2  u_sunPos;',
    'uniform float u_sunSize;',
    'uniform float u_mist;',
    'uniform float u_trees;',

    'float hash11(float p) {',
    '  p = fract(p * 0.1031);',
    '  p *= p + 33.33;',
    '  p *= p + p;',
    '  return fract(p);',
    '}',

    'float hash21(vec2 p) {',
    '  vec3 p3 = fract(vec3(p.xyx) * 0.1031);',
    '  p3 += dot(p3, p3.yzx + 33.33);',
    '  return fract((p3.x + p3.y) * p3.z);',
    '}',

    'float noise11(float x) {',
    '  float i = floor(x);',
    '  float f = fract(x);',
    '  f = f * f * (3.0 - 2.0 * f);',
    '  return mix(hash11(i), hash11(i + 1.0), f);',
    '}',

    'float noise21(vec2 p) {',
    '  vec2 i = floor(p);',
    '  vec2 f = fract(p);',
    '  f = f * f * (3.0 - 2.0 * f);',
    '  float a = hash21(i);',
    '  float b = hash21(i + vec2(1.0, 0.0));',
    '  float c = hash21(i + vec2(0.0, 1.0));',
    '  float d = hash21(i + vec2(1.0, 1.0));',
    '  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);',
    '}',

    'float fbm11(float x) {',
    '  float sum = 0.0;',
    '  float amp = 0.5;',
    '  for (int i = 0; i < 3; i++) {',
    '    sum += amp * noise11(x);',
    '    x = x * 2.03 + 1.7;',
    '    amp *= 0.5;',
    '  }',
    '  return sum;',
    '}',

    'float fbm21(vec2 p) {',
    '  float sum = 0.0;',
    '  float amp = 0.5;',
    '  for (int i = 0; i < 3; i++) {',
    '    sum += amp * noise21(p);',
    '    p = p * 2.02 + vec2(1.3, 2.1);',
    '    amp *= 0.5;',
    '  }',
    '  return sum;',
    '}',

    // Ridge silhouette height for a layer. depth: 0.0 = furthest, 1.0 = nearest.
    'float ridgeBase(float depth) { return mix(0.47, 0.17, depth); }',
    'float ridgeAmp(float depth) { return mix(0.05, 0.15, depth); }',

    'float ridgeHeight(float x, float depth, float seed) {',
    '  float freq = mix(2.6, 1.05, depth);',
    '  float coarse = fbm11(x * freq + seed) - 0.47;',
    '  float fine = fbm11(x * freq * 3.3 + seed * 3.1) - 0.47;',
    '  return ridgeBase(depth) + ridgeAmp(depth) * (coarse * 1.6 + fine * 0.5);',
    '}',

    // A stand of conifers: repeating tapered spikes with per-tree jitter.
    'float treeLine(float x, float spacing, float seed) {',
    '  float id = floor(x / spacing);',
    '  float tall = hash11(id + seed);',
    '  float wide = hash11(id * 1.37 + seed + 9.1);',
    '  float local = (fract(x / spacing) - 0.5) * 2.0;',
    '  float halfWidth = 0.5 + 0.42 * wide;',
    '  float spike = clamp(1.0 - abs(local) / halfWidth, 0.0, 1.0);',
    '  return pow(spike, 0.7) * (0.45 + 0.95 * tall);',
    '}',

    'void main() {',
    '  vec2 uv = gl_FragCoord.xy / u_res;',
    '  float aspect = u_res.x / u_res.y;',
    '  float t = u_time;',
    '  vec2 par = u_pointer;',

    // Sky: three-stop vertical wash, weighted so the warm horizon light survives
    // just above the ridge line instead of hiding behind the mountains.
    '  vec3 col = mix(u_skyLow, u_skyMid, smoothstep(0.26, 0.72, uv.y));',
    '  col = mix(col, u_skyTop, smoothstep(0.56, 1.06, uv.y));',
    '  float horizonBand = exp(-abs(uv.y - 0.46) * 6.0);',
    '  horizonBand *= 0.4 + 0.6 * exp(-abs(uv.x - u_sunPos.x) * 1.7);',
    '  col = mix(col, u_skyLow, horizonBand * 0.5);',

    // Sun with a tight halo, a wide bloom and a soft disc edge.
    '  vec2 sunAt = u_sunPos + vec2(par.x * -0.012, par.y * -0.009);',
    '  float sunDist = length((uv - sunAt) * vec2(aspect, 1.0));',
    '  float size = max(u_sunSize, 0.015);',
    '  float halo = exp(-sunDist * (2.0 / size));',
    '  float bloom = exp(-sunDist * (0.55 / size));',
    '  col += u_sunCol * halo * 0.6;',
    '  col += u_sunCol * bloom * 0.22;',
    '  float disc = smoothstep(size, size * 0.9, sunDist) * (0.93 + 0.07 * sin(t * 0.4));',
    '  col = mix(col, u_sunCol + 0.05, disc);',

    // Cloud bands: stretched noise drifting slowly, lit from the sun side.
    '  if (uv.y > 0.24) {',
    '    vec2 cp = vec2(uv.x * 1.7 + t * 0.008, uv.y * 5.5 - t * 0.002);',
    '    float cloud = fbm21(cp);',
    '    float band = smoothstep(0.5, 0.85, cloud) * smoothstep(0.28, 0.68, uv.y);',
    '    band *= 1.0 - smoothstep(0.84, 1.0, uv.y);',
    '    vec3 cloudCol = mix(u_skyLow, u_sunCol, 0.3 + 0.45 * halo);',
    '    col = mix(col, cloudCol, band * 0.5);',
    '  }',

    '#ifdef USE_STARS',
    // Square cells, so stars stay round instead of stretching with the viewport.
    '  vec2 grid = vec2(105.0 * aspect, 105.0);',
    '  vec2 cell = floor(uv * grid);',
    '  float pick = hash21(cell);',
    '  if (pick > 0.9) {',
    '    vec2 within = fract(uv * grid) - 0.5;',
    '    float bright = hash11(pick * 41.0);',
    '    float radius = 0.09 + 0.13 * bright;',
    '    float twinkle = 0.55 + 0.45 * sin(t * 1.7 + pick * 60.0);',
    '    float star = smoothstep(radius, 0.0, length(within));',
    '    float fade = smoothstep(0.18, 0.62, uv.y);',
    '    col += vec3(0.86, 0.91, 1.0) * star * twinkle * fade * (0.35 + 0.75 * bright);',
    '  }',
    '#endif',

    '  float aa = 1.4 / u_res.y;',
    '  vec3 mistCol = mix(u_skyLow, u_skyMid, 0.22) + u_sunCol * halo * 0.3;',

    '  for (int i = 0; i < LAYERS; i++) {',
    '    float depth = float(i) / float(LAYERS - 1);',
    '    float shiftX = par.x * mix(0.006, 0.05, depth);',
    '    float shiftY = par.y * mix(0.004, 0.02, depth) - u_scroll * mix(0.01, 0.09, depth);',

    // Skip the whole layer for pixels that sit well above its tallest possible peak.
    '    float ceiling = ridgeBase(depth) + ridgeAmp(depth) * 1.7 + 0.04 + shiftY;',
    '    if (uv.y > ceiling + 0.06) { continue; }',

    '    float h = ridgeHeight(uv.x + shiftX + float(i) * 7.3, depth, float(i) * 3.7) + shiftY;',

    '#ifdef USE_TREES',
    '    float treeAmt = u_trees * smoothstep(0.42, 1.0, depth);',
    '    if (treeAmt > 0.002) {',
    '      float tx = uv.x + shiftX;',
    '      float near = treeLine(tx, mix(0.011, 0.018, depth), float(i) * 5.1);',
    '      float back = treeLine(tx + 0.004, mix(0.006, 0.011, depth), float(i) * 5.1 + 3.3);',
    '      h += treeAmt * (0.015 + 0.013 * depth) * max(near, back * 0.7);',
    '    }',
    '#endif',

    // Mist pooling on top of the ridge, animated so the haze keeps moving.
    '    float above = uv.y - h;',
    '    if (above > 0.0 && above < 0.34) {',
    '      float band = exp(-above * mix(24.0, 11.0, depth));',
    '      float drift = fbm21(vec2(uv.x * 2.1 + t * 0.02 * (1.0 + depth), uv.y * 7.0 + t * 0.012));',
    '      band *= 0.55 + 0.8 * drift;',
    '      col = mix(col, mistCol, clamp(band * u_mist * mix(0.95, 0.38, depth), 0.0, 1.0));',
    '    }',

    // Aerial perspective: distant ridges wash toward the mist colour overall, and
    // every ridge catches a little haze right along its top edge.
    '    vec3 rc = mix(u_ridgeFar, u_ridgeNear, pow(depth, 0.85));',
    '    rc = mix(rc, mistCol, (1.0 - depth) * 0.2);',
    '    rc = mix(rc, mistCol, (1.0 - depth) * 0.45 * (1.0 - smoothstep(0.0, 0.3, h - uv.y)));',
    '    col = mix(col, rc, smoothstep(h + aa, h - aa, uv.y));',
    '  }',

    // Vignette plus a touch of dither, which also kills gradient banding.
    '  vec2 vc = (uv - 0.5) * vec2(aspect, 1.0);',
    '  col *= 1.0 - 0.17 * dot(vc, vc);',
    '  col += (hash21(gl_FragCoord.xy + fract(t) * 91.7) - 0.5) * 0.018;',

    '  gl_FragColor = vec4(col, 1.0);',
    '}'
  ].join('\n');

  var UNIFORM_NAMES = [
    'u_res', 'u_time', 'u_pointer', 'u_scroll', 'u_skyTop', 'u_skyMid', 'u_skyLow',
    'u_sunCol', 'u_ridgeFar', 'u_ridgeNear', 'u_sunPos', 'u_sunSize', 'u_mist', 'u_trees'
  ];

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function readNumber(el, name, fallback) {
    var raw = parseFloat(el.getAttribute(name));
    return isNaN(raw) ? fallback : raw;
  }

  // Accepts #rgb, #rrggbb, rgb() and rgba() so any Shopify colour setting works.
  function toRgb(input, fallback) {
    var value = (input || '').trim();
    if (value.charAt(0) === '#') {
      var hex = value.slice(1);
      if (hex.length === 3) {
        hex = hex.charAt(0) + hex.charAt(0) + hex.charAt(1) + hex.charAt(1) + hex.charAt(2) + hex.charAt(2);
      }
      if (hex.length >= 6) {
        var int = parseInt(hex.slice(0, 6), 16);
        if (!isNaN(int)) {
          return [((int >> 16) & 255) / 255, ((int >> 8) & 255) / 255, (int & 255) / 255];
        }
      }
    }
    var parts = value.match(/[\d.]+/g);
    if (parts && parts.length >= 3) {
      return [parts[0] / 255, parts[1] / 255, parts[2] / 255];
    }
    return fallback;
  }

  function compile(gl, type, source) {
    var shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      if (window.console) console.warn('[mountain-scene] shader failed to compile', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  function defineElement() {
    class Scene extends HTMLElement {
      connectedCallback() {
        if (this.booted) return;
        this.booted = true;
        // Reduced motion still gets the scene, just frozen on a single frame.
        this.staticOnly =
          this.getAttribute('data-static') === 'true' ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.start();
      }

      disconnectedCallback() {
        this.stop();
        this.booted = false;
      }

      start() {
        var small = window.matchMedia('(max-width: 749px)').matches;
        this.maxDpr = small ? 1.0 : 1.5;
        this.scale = 1;
        this.speed = clamp(readNumber(this, 'data-speed', 100) / 100, 0, 3);
        this.parallaxOn =
          !this.staticOnly &&
          this.getAttribute('data-parallax') !== 'false' &&
          window.matchMedia('(hover: hover) and (pointer: fine)').matches;

        this.canvas = document.createElement('canvas');
        this.canvas.setAttribute('aria-hidden', 'true');
        var options = {
          alpha: false,
          antialias: false,
          depth: false,
          stencil: false,
          powerPreference: 'low-power',
          // A frame that is not continuously redrawn would otherwise be discarded
          // after compositing: true for a one-shot static frame, and for a page that
          // starts hidden (prerender, background tab) where the loop is paused.
          preserveDrawingBuffer: this.staticOnly === true || document.hidden === true
        };
        var gl = this.canvas.getContext('webgl', options) || this.canvas.getContext('experimental-webgl', options);
        if (!gl) return;
        this.gl = gl;

        if (!this.buildProgram()) {
          this.gl = null;
          return;
        }

        this.appendChild(this.canvas);
        this.pointer = { x: 0, y: 0, tx: 0, ty: 0 };
        this.scroll = 0;
        this.top = 0;
        this.height = 1;
        this.visible = true;
        this.paused = document.hidden;
        this.frames = 0;
        this.slowFrames = 0;
        this.minDelta = 1000 / 31;
        this.lastFrame = 0;
        this.startedAt = 0;

        this.bind();
        this.measure();
        this.resize();

        // Draw before revealing the canvas. An undrawn WebGL surface composites as
        // opaque black, which would hide the SVG whenever the first animation frame
        // is delayed — on a background tab the loop is paused and never draws at all.
        this.draw(0);
        this.classList.add('is-live');

        if (this.staticOnly) return;

        this.loop = this.loop.bind(this);
        this.raf = requestAnimationFrame(this.loop);
      }

      buildProgram() {
        var gl = this.gl;
        var layers = Math.round(clamp(readNumber(this, 'data-layers', 5), 2, 6));
        var defines = ['#define LAYERS ' + layers];
        if (readNumber(this, 'data-trees', 70) > 0) defines.push('#define USE_TREES');
        if (this.getAttribute('data-stars') === 'true') defines.push('#define USE_STARS');

        var vertex = compile(gl, gl.VERTEX_SHADER, VERTEX);
        var fragment = compile(gl, gl.FRAGMENT_SHADER, defines.join('\n') + '\n' + FRAGMENT);
        if (!vertex || !fragment) return false;

        var program = gl.createProgram();
        gl.attachShader(program, vertex);
        gl.attachShader(program, fragment);
        gl.linkProgram(program);
        gl.deleteShader(vertex);
        gl.deleteShader(fragment);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
          if (window.console) console.warn('[mountain-scene] program failed to link', gl.getProgramInfoLog(program));
          return false;
        }

        gl.useProgram(program);
        this.program = program;

        var buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
        var attribute = gl.getAttribLocation(program, 'a_pos');
        gl.enableVertexAttribArray(attribute);
        gl.vertexAttribPointer(attribute, 2, gl.FLOAT, false, 0, 0);

        this.uniforms = {};
        for (var i = 0; i < UNIFORM_NAMES.length; i++) {
          this.uniforms[UNIFORM_NAMES[i]] = gl.getUniformLocation(program, UNIFORM_NAMES[i]);
        }
        this.setStaticUniforms();
        return true;
      }

      setStaticUniforms() {
        var gl = this.gl;
        var u = this.uniforms;
        gl.uniform3fv(u.u_skyTop, toRgb(this.getAttribute('data-sky-top'), [0.13, 0.19, 0.24]));
        gl.uniform3fv(u.u_skyMid, toRgb(this.getAttribute('data-sky-mid'), [0.43, 0.55, 0.6]));
        gl.uniform3fv(u.u_skyLow, toRgb(this.getAttribute('data-sky-low'), [0.91, 0.79, 0.66]));
        gl.uniform3fv(u.u_sunCol, toRgb(this.getAttribute('data-sun'), [0.96, 0.85, 0.66]));
        gl.uniform3fv(u.u_ridgeFar, toRgb(this.getAttribute('data-ridge-far'), [0.49, 0.59, 0.64]));
        gl.uniform3fv(u.u_ridgeNear, toRgb(this.getAttribute('data-ridge-near'), [0.11, 0.16, 0.14]));
        gl.uniform2f(
          u.u_sunPos,
          clamp(readNumber(this, 'data-sun-x', 68) / 100, 0, 1),
          clamp(readNumber(this, 'data-sun-y', 62) / 100, 0, 1)
        );
        gl.uniform1f(u.u_sunSize, clamp(readNumber(this, 'data-sun-size', 5) / 100, 0.01, 0.3));
        gl.uniform1f(u.u_mist, clamp(readNumber(this, 'data-mist', 60) / 100, 0, 1.4));
        gl.uniform1f(u.u_trees, clamp(readNumber(this, 'data-trees', 70) / 100, 0, 1.5));
      }

      bind() {
        var self = this;

        this.onScroll = function () {
          self.scroll = clamp((window.pageYOffset - self.top) / self.height, 0, 1);
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });

        this.onResize = function () {
          self.measure();
          self.resize();
          if (self.staticOnly) self.draw(0);
        };
        window.addEventListener('resize', this.onResize);

        this.onVisibility = function () {
          self.paused = document.hidden;
        };
        document.addEventListener('visibilitychange', this.onVisibility);

        if (this.parallaxOn) {
          this.onPointer = function (event) {
            var rect = self.getBoundingClientRect();
            if (!rect.width || !rect.height) return;
            self.pointer.tx = clamp(((event.clientX - rect.left) / rect.width) * 2 - 1, -1, 1);
            self.pointer.ty = clamp(((event.clientY - rect.top) / rect.height) * -2 + 1, -1, 1);
          };
          this.onLeave = function () {
            self.pointer.tx = 0;
            self.pointer.ty = 0;
          };
          var target = this.closest('.hero') || this;
          this.pointerTarget = target;
          target.addEventListener('pointermove', this.onPointer, { passive: true });
          target.addEventListener('pointerleave', this.onLeave);
        }

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(function (entries) {
            self.visible = entries[0].isIntersecting;
          });
          this.observer.observe(this);
        }

        this.onContextLost = function (event) {
          event.preventDefault();
          self.stop();
        };
        this.onContextRestored = function () {
          self.booted = false;
          self.connectedCallback();
        };
        this.canvas.addEventListener('webglcontextlost', this.onContextLost);
        this.canvas.addEventListener('webglcontextrestored', this.onContextRestored);
      }

      measure() {
        var rect = this.getBoundingClientRect();
        this.top = rect.top + window.pageYOffset;
        this.height = Math.max(rect.height, 1);
        this.onScroll();
      }

      resize() {
        if (!this.gl) return;
        var dpr = Math.min(window.devicePixelRatio || 1, this.maxDpr) * this.scale;
        var width = Math.max(Math.round(this.clientWidth * dpr), 1);
        var height = Math.max(Math.round(this.clientHeight * dpr), 1);
        if (width === this.canvas.width && height === this.canvas.height) return;
        this.canvas.width = width;
        this.canvas.height = height;
        this.gl.viewport(0, 0, width, height);
        this.gl.uniform2f(this.uniforms.u_res, width, height);
      }

      // If the GPU cannot keep up, drop resolution once rather than run at 15fps.
      watchPerformance(delta) {
        if (this.scale <= 0.6) return;
        this.frames++;
        if (delta > 34) this.slowFrames++;
        if (this.frames < 90) return;
        if (this.slowFrames > 40) {
          this.scale = this.scale > 0.8 ? 0.75 : 0.6;
          this.canvas.width = 0;
          this.resize();
        }
        this.frames = 0;
        this.slowFrames = 0;
      }

      draw(seconds) {
        var gl = this.gl;
        if (!gl) return;
        gl.uniform1f(this.uniforms.u_time, seconds);
        gl.uniform2f(this.uniforms.u_pointer, this.pointer.x, this.pointer.y);
        gl.uniform1f(this.uniforms.u_scroll, this.staticOnly ? 0 : this.scroll);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      }

      loop(now) {
        this.raf = requestAnimationFrame(this.loop);
        if (!this.gl || this.paused || !this.visible) return;

        var delta = now - this.lastFrame;
        if (delta < this.minDelta) return;
        this.lastFrame = now;
        if (!this.startedAt) this.startedAt = now;
        this.watchPerformance(delta);
        this.resize();

        this.pointer.x += (this.pointer.tx - this.pointer.x) * 0.05;
        this.pointer.y += (this.pointer.ty - this.pointer.y) * 0.05;

        this.draw(((now - this.startedAt) / 1000) * this.speed);
      }

      stop() {
        if (this.raf) cancelAnimationFrame(this.raf);
        this.raf = null;
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
        if (this.pointerTarget && this.onPointer) {
          this.pointerTarget.removeEventListener('pointermove', this.onPointer);
          this.pointerTarget.removeEventListener('pointerleave', this.onLeave);
        }
        if (this.observer) this.observer.disconnect();
        if (this.canvas) {
          this.canvas.removeEventListener('webglcontextlost', this.onContextLost);
          this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored);
          if (this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas);
        }
        this.classList.remove('is-live');
        this.gl = null;
        this.canvas = null;
      }
    }

    customElements.define('mountain-scene', Scene);
  }

  if ('customElements' in window && !customElements.get('mountain-scene')) {
    defineElement();
  }
})();
